import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import store, { borrowerActions } from '@apply/adminStore';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { formatPhone } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';

export const EntitySelector = ({
    entityId,
    excludedPersonId = 0,
    onSelect = null,
    disabled = false,
    required = true,
    canEdit = true,
    showTitle = true,
    title = 'Entity',
    isValid = true,
}) => {
    const PER_PAGE = 20;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const { dispatch } = store;

    //Borrower Search
    const [cache, set_cache] = useState({});
    const [searchLoading, set_searchLoading] = useState(false);
    const [entities, set_entities] = useState([]);
    const [query, set_query] = useState('');
    const [entity, set_entity] = useState();

    useEffect(() => {
        // Do we need to await when no code follows?
        async function load() {
            await loadEntity(entityId);
        }

        load();
    }, [entityId]);

    const loadEntity = async (id) => {
        if (!id) {
            set_entity(null);
            return;
        }

        const response = await borrowerApi.entitiesTypeAheadById(id);
        if (response.statusCode !== 200) return;
        const bwr = response.result;
        set_entity(bwr);
        return bwr;
    };

    const addEntity = () => {
        dispatch(
            borrowerActions.setEditEntityModal({
                editEntityModal: {
                    isOpen: true,
                    entityId: 0,
                    onChange: afterSave,
                },
            })
        );
    };

    const doSearch = async (query, page = 1) => {
        const model = {
            searchText: query,
            take: PER_PAGE,
            skip: (page - 1) * PER_PAGE,
            excludedPersonId,
        };
        return await borrowerApi.entitiesTypeAhead(model);
    };

    const handleInputChange = (q) => {
        set_entity();
        q = (q ?? '').toLowerCase();
        set_query(q);
    };

    const handleSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (cache[q]) {
            set_entities(cache[q].options);
            return;
        }

        set_searchLoading(true);
        doSearch(q).then((resp) => {
            cache[q] = { options: resp.result, page: 1 };
            set_cache(cache);
            set_searchLoading(false);
            set_entities(resp.result);
        });
    }, []);

    const handlePagination = (e, shownResults) => {
        const cachedQuery = cache[query];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);
        const page = cachedQuery.page + 1;
        doSearch(query, page).then((resp) => {
            const options = cachedQuery.options.concat(resp.result);
            cache[query] = { ...cachedQuery, options, page };
            set_cache(cache);
            set_searchLoading(false);
            set_entities(options);
        });
    };

    const onEntitySelect = async (enty) => {
        const fullEntity = await loadEntity(enty?.entityId);
        onSelect && onSelect(fullEntity, cancelSelect);
    };

    const cancelSelect = () => {
        set_entity(null);
    };

    const clearEntity = () => {
        set_entity(null);
        onSelect && onSelect(null, cancelSelect);
    };

    const afterSave = (enty) => {
        onEntitySelect(enty);
        set_cache({});
    };

    const editEntity = (entityId) => {
        dispatch(
            borrowerActions.setEditEntityModal({
                editEntityModal: {
                    isOpen: true,
                    entityId,
                    onChange: afterSave,
                },
            })
        );
    };

    const onRenderPhone = (e) => {
        return formatPhone(e.value);
    };

    const filterByFields = ['name', 'tin'];

    return (
        <div>
            <div className='type-ahead-container'>
                {showTitle && (
                    <label htmlFor='borrower-search'>
                        {title} {required && <span className='req'>*</span>}
                    </label>
                )}
                <AsyncTypeahead
                    className={!isValid ? 'type-ahead not-valid' : 'type-ahead'}
                    id='borrower-search'
                    isLoading={searchLoading}
                    labelKey={(option) => `${option?.name}`}
                    maxResults={PER_PAGE - 1}
                    minLength={2}
                    onInputChange={handleInputChange}
                    onPaginate={handlePagination}
                    onSearch={handleSearch}
                    onChange={(event) => onEntitySelect(event[0])}
                    options={entities}
                    paginate
                    placeholder='Search Borrower Entities by Name or TIN...'
                    renderMenuItemChildren={(option) => (
                        <div
                            key={option.id}
                            style={{ borderBottom: '1px solid silver' }}
                        >
                            <span>
                                <div>{option.name}</div>
                                {option.tin && <div>{option.tin}</div>}
                                {(option.city || option.state) && (
                                    <span>
                                        {option?.city}, {option?.state}
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    useCache={false}
                    option
                    selected={entity ? [entity] : []}
                    disabled={disabled}
                    inputProps={{ required: required }}
                    filterBy={filterByFields}
                />
                <button
                    type='button'
                    className='type-ahead-clear-button'
                    onClick={() => clearEntity()}
                    disabled={disabled}
                    title='Remove selected entity'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                <button
                    type='button'
                    onClick={addEntity}
                    className='button type-ahead-add-button'
                    title='Add a new entity'
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>

            {entity && (
                <div
                    style={{
                        border: '1px solid silver',
                        borderRadius: '10px',
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        width: '100%',
                    }}
                >
                    <div className='row'>
                        <div className='columns small-12 large-5'>
                            {canEdit && (
                                <div style={{ float: 'right' }}>
                                    <button
                                        type='button'
                                        onClick={() => editEntity(entity.entityId)}
                                        className='button hollow'
                                        title='Edit Borrower Details'
                                        style={{
                                            verticalAlign: 'unset',
                                            marginBottom: '0',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                </div>
                            )}
                            {entity.name && (
                                <div>
                                    <b>{entity.name}</b>
                                </div>
                            )}
                            {entity.street1 && <div>{entity.street1}</div>}
                            {entity.street2 && <div>{entity.street2}</div>}
                            {entity.city && entity.state && entity.zipCode && (
                                <div>
                                    {entity.city}, {entity.state} {entity.zipCode}
                                </div>
                            )}
                            {entity.tin && <div>{entity.tin}</div>}
                        </div>

                        <div className='columns small-12 large-7'>
                            {entity.principals && entity.principals.length > 0 && (
                                <div>
                                    Principals:
                                    <DataGrid
                                        dataSource={entity.principals}
                                        showBorders={true}
                                        showRowLines={false}
                                        rowAlternationEnabled={true}
                                        remoteOperations={false}
                                        allowColumnReordering={false}
                                        allowColumnResizing={true}
                                        cellHintEnabled={true}
                                    >
                                        <Column
                                            dataField='name'
                                            caption='Name'
                                        />
                                        <Column
                                            dataField='phone'
                                            caption='Phone'
                                            width='100'
                                            cellRender={onRenderPhone}
                                        />
                                        <Column
                                            dataField='email'
                                            caption='Email'
                                            width='230'
                                        />
                                    </DataGrid>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
